import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  createJob,
  createProject,
  createEvent,
  getJobs,
  getProjects,
  getEvents,
  createForumPost,
  getForumPosts,
  createGroup,
  getGroups,
  getAnalytics
} from '../services/firestoreService';

export default function FirebaseTest() {
  const { currentUser, userProfile, logout } = useAuth();
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const addTestResult = (message, success = true) => {
    setTestResults(prev => [...prev, { message, success, timestamp: new Date().toLocaleTimeString() }]);
  };

  const testFirebaseConnection = async () => {
    setLoading(true);
    setTestResults([]);

    try {
      addTestResult('🚀 Starting comprehensive Firebase backend test...');

      // Test 1: Check authentication
      if (currentUser) {
        addTestResult('✅ Authentication: User is logged in');
        addTestResult(`✅ User ID: ${currentUser.uid}`);
        addTestResult(`✅ Email: ${currentUser.email}`);
      } else {
        addTestResult('❌ Authentication: No user logged in', false);
        return;
      }

      // Test 2: Check user profile
      if (userProfile) {
        addTestResult('✅ User Profile: Profile data loaded');
        addTestResult(`✅ Display Name: ${userProfile.displayName}`);
        addTestResult(`✅ Status: ${userProfile.status}`);
        addTestResult(`✅ Role: ${userProfile.role}`);
        addTestResult(`✅ Company: ${userProfile.company}`);
      } else {
        addTestResult('❌ User Profile: No profile data', false);
      }

      // Test 3: Test reading existing data
      try {
        const [jobs, projects, events] = await Promise.all([
          getJobs(5),
          getProjects(5),
          getEvents(5)
        ]);

        addTestResult(`✅ Data Reading: Found ${jobs.length} jobs`);
        addTestResult(`✅ Data Reading: Found ${projects.length} projects`);
        addTestResult(`✅ Data Reading: Found ${events.length} events`);
      } catch (error) {
        addTestResult(`❌ Data Reading: ${error.message}`, false);
      }

      // Test 4: Test creating a job (if user is approved)
      if (userProfile?.status === 'approved') {
        try {
          const jobData = {
            title: 'Test Job - Firebase Integration',
            company: 'Test Company',
            location: 'Remote',
            description: 'This is a test job created to verify Firebase integration.',
            jobType: 'Full-time',
            workType: 'Remote',
            salary: '$50,000 - $70,000',
            requirements: ['Test requirement 1', 'Test requirement 2'],
            benefits: ['Health insurance', 'Remote work']
          };

          const jobId = await createJob(jobData, currentUser.uid);
          addTestResult(`✅ Job Creation: Successfully created job with ID: ${jobId}`);
        } catch (error) {
          addTestResult(`❌ Job Creation: ${error.message}`, false);
        }
      } else {
        addTestResult('⚠️ Job Creation: Skipped (requires approved status)');
      }

      // Test 5: Test creating a project
      if (userProfile?.status === 'approved') {
        try {
          const projectData = {
            title: 'Test Project - Firebase Integration',
            description: 'This is a test project created to verify Firebase integration.',
            budgetRange: '$1,000 - $5,000',
            timeline: '1-3 months',
            skills: ['JavaScript', 'React', 'Firebase'],
            projectType: 'One-time',
            urgency: 'Medium',
            deliverables: ['Working application', 'Documentation']
          };

          const projectId = await createProject(projectData, currentUser.uid);
          addTestResult(`✅ Project Creation: Successfully created project with ID: ${projectId}`);
        } catch (error) {
          addTestResult(`❌ Project Creation: ${error.message}`, false);
        }
      } else {
        addTestResult('⚠️ Project Creation: Skipped (requires approved status)');
      }

      // Test 6: Test creating a forum post
      if (userProfile?.status === 'approved') {
        try {
          const postData = {
            title: 'Test Forum Post - Firebase Integration',
            content: 'This is a test forum post to verify Firebase integration.',
            category: 'General Discussion'
          };

          const postId = await createForumPost(postData, currentUser.uid);
          addTestResult(`✅ Forum Post Creation: Successfully created post with ID: ${postId}`);
        } catch (error) {
          addTestResult(`❌ Forum Post Creation: ${error.message}`, false);
        }
      } else {
        addTestResult('⚠️ Forum Post Creation: Skipped (requires approved status)');
      }

      // Test 7: Test creating a group
      if (userProfile?.status === 'approved') {
        try {
          const groupData = {
            name: 'Test Group - Firebase Integration',
            description: 'This is a test group created to verify Firebase integration.',
            category: 'Technology',
            isPrivate: false
          };

          const groupId = await createGroup(groupData, currentUser.uid);
          addTestResult(`✅ Group Creation: Successfully created group with ID: ${groupId}`);
        } catch (error) {
          addTestResult(`❌ Group Creation: ${error.message}`, false);
        }
      } else {
        addTestResult('⚠️ Group Creation: Skipped (requires approved status)');
      }

      // Test 8: Test creating an event (admin only)
      if (userProfile?.role === 'admin') {
        try {
          const eventData = {
            title: 'Test Event - Firebase Integration',
            description: 'This is a test event created to verify Firebase integration.',
            startDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 1 week from now
            endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000 + 2 * 60 * 60 * 1000), // 2 hours later
            location: 'Virtual Event Platform',
            isVirtual: true,
            maxAttendees: 100,
            eventType: 'Workshop',
            pricing: {
              member: 0,
              nonMember: 25,
              sponsorDiscount: 10
            }
          };

          const eventId = await createEvent(eventData);
          addTestResult(`✅ Event Creation: Successfully created event with ID: ${eventId}`);
        } catch (error) {
          addTestResult(`❌ Event Creation: ${error.message}`, false);
        }
      } else {
        addTestResult('⚠️ Event Creation: Skipped (requires admin role)');
      }

      // Test 9: Test analytics (admin only)
      if (userProfile?.role === 'admin') {
        try {
          const analytics = await getAnalytics();
          addTestResult(`✅ Analytics: Retrieved platform statistics`);
          addTestResult(`✅ Analytics: ${analytics.totalUsers} total users`);
          addTestResult(`✅ Analytics: ${analytics.activeMembers} active members`);
          addTestResult(`✅ Analytics: ${analytics.pendingMembers} pending members`);
        } catch (error) {
          addTestResult(`❌ Analytics: ${error.message}`, false);
        }
      } else {
        addTestResult('⚠️ Analytics: Skipped (requires admin role)');
      }

      // Test 10: Test forum data retrieval
      try {
        const forumPosts = await getForumPosts(5);
        addTestResult(`✅ Forum Data: Retrieved ${forumPosts.length} forum posts`);
      } catch (error) {
        addTestResult(`❌ Forum Data: ${error.message}`, false);
      }

      // Test 11: Test groups data retrieval
      try {
        const groups = await getGroups(5);
        addTestResult(`✅ Groups Data: Retrieved ${groups.length} groups`);
      } catch (error) {
        addTestResult(`❌ Groups Data: ${error.message}`, false);
      }

      addTestResult('🎉 Comprehensive Firebase backend test completed!');
      addTestResult('📊 Check your Firebase console to verify all data was created correctly.');

    } catch (error) {
      addTestResult(`❌ General Error: ${error.message}`, false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      addTestResult('✅ Logout: Successfully logged out');
    } catch (error) {
      addTestResult(`❌ Logout Error: ${error.message}`, false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Firebase Backend Test</h2>
          <p className="mt-2 text-gray-600">
            Test the Firebase backend integration and verify all services are working correctly.
          </p>
        </div>

        <div className="p-6">
          {/* User Info */}
          {currentUser && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Current User</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Email:</span> {currentUser.email}
                </div>
                <div>
                  <span className="font-medium">UID:</span> {currentUser.uid}
                </div>
                {userProfile && (
                  <>
                    <div>
                      <span className="font-medium">Display Name:</span> {userProfile.displayName}
                    </div>
                    <div>
                      <span className="font-medium">Status:</span>
                      <span className={`ml-2 px-2 py-1 rounded text-xs ${
                        userProfile.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {userProfile.status}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Role:</span> {userProfile.role}
                    </div>
                    <div>
                      <span className="font-medium">Company:</span> {userProfile.company}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Test Controls */}
          <div className="flex space-x-4 mb-6">
            <button
              onClick={testFirebaseConnection}
              disabled={loading}
              className="bg-apn-blue text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Running Tests...' : 'Run Firebase Tests'}
            </button>

            {currentUser && (
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700"
              >
                Logout
              </button>
            )}
          </div>

          {/* Test Results */}
          {testResults.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Test Results</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {testResults.map((result, index) => (
                  <div
                    key={index}
                    className={`flex items-start space-x-2 text-sm ${
                      result.success ? 'text-green-700' : 'text-red-700'
                    }`}
                  >
                    <span className="text-xs text-gray-500 mt-0.5 min-w-[60px]">
                      {result.timestamp}
                    </span>
                    <span className="flex-1">{result.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="mt-8 p-4 bg-yellow-50 rounded-lg">
            <h3 className="text-lg font-semibold text-yellow-900 mb-2">Instructions</h3>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• Make sure you're logged in to run the full test suite</li>
              <li>• The test will create sample data in your Firebase database</li>
              <li>• Event creation requires admin role</li>
              <li>• Check your Firebase console to verify data was created</li>
              <li>• You can safely delete test data after verification</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
